import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts/highstock';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

let highchartUtils = require('../../../util/highcharts')(Highcharts);

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  navigator: {
    enabled: false
  },
  rangeSelector: {
    enabled: false
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    tickmarkPlacement: 'on',
    title: {
      enable: false
    }
  },
  yAxis: {
    opposite: false,
    allowDecimals: false,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    gridLineColor: '#ececec',
    gridLineWidth: 1,
    title: {
      text: 'Daily Throughput',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      shadow: false,
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    column: {
      stacking: 'normal',
      pointPadding: 0,
      borderWidth: 1,
      groupPadding: 0,
      shadow: false,
      pointRange: 24 * 3600 * 1000,
      states: {
        hover: {
          brightness: -0.05
        }
      },
      dataLabels: {
        enabled: false,
        style: {
          fontSize: '11px',
          fontWeight: 'bold',
          color: 'contrast'
        },
        formatter: function() {
          if (!this.percentage) {
            return null;
          }
          return `${Highcharts.numberFormat(this.percentage)}% (${this.y})`;
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};

export class ThroughputChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount = () => {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    require('../../../util/highcharts-regression')(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this, that);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.chart.reflow();
    }

    if (prevProps.breakdown != this.props.breakdown) {
      this.togglePercentage();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename
          }
        },
        false
      );
    }

    if (prevProps.trendLine != this.props.trendLine) {
      this.updateRegression();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart(oldData = []) {
    this.chart.xAxis[0].setExtremes(null, null);
    const diff = dataUtils.getDiff(oldData, this.props.data, 'data');
    highchartUtils.updateSeries(diff, this.chart);

    this.updateTimeUnit(this.props.timeUnit);
    this.chart.redraw();
    this.chart.xAxis[0].setExtremes(null,null);
    this.togglePercentage();
    this.updateRegression();
  }

  togglePercentage() {
    let enabled = false;
    if (this.props.breakdown && this.chart.series.length && this.chart.series[0].columnMetrics) {
      enabled = this.chart.series[0].columnMetrics.width >= 40;
    }

    this.chart.update({
      plotOptions: {
        column: {
          dataLabels: {
            enabled
          }
        }
      }
    });
  }

  updateRegression() {
    const trend = _.find(this.chart.series, { name: 'trend' });
    if (trend) {
      this.chart.series[trend.index].remove(true);
    }
    if (this.props.trendLine) {
      const data = _([...this.props.data])
        .flatMap('data')
        .groupBy('x')
        .map((points, x) => {
          return {
            x: +x,
            y: _.reduce(
              points,
              (acc, p) => {
                return acc + p.y;
              },
              0
            )
          };
        })
        .value();

      this.chart.addSeries(
        {
          data,
          visible: false,
          regression: true,
          regressionSettings: {
            name: 'trend',
            useAllSeries: false,
            type: 'polynomial',
            order: 6,
            color: 'rgb(87, 181, 70)',
            lineWidth: 3,
            dashStyle: 'LongDashDot',
            tooltip: {
              enabled: false
            }
          }
        },
        true
      );
    }
  }

  updateTimeUnit(timeUnit) {
    let text = `Daily Throughput`,
      days = 1,
      ordinal = true,
      placement,
      min,
      max;

    const sorted = _([...this.props.data])
      .flatMap('data')
      .orderBy('x')
      .map('x');
    min = sorted.first();
    max = sorted.last();

    if (moment(min).isSameOrAfter(this.props.dates.startDate)) {
      min = this.props.dates.startDate;
    }
    if (moment(max).isSameOrAfter(this.props.dates.endDate)) {
      max = this.props.dates.endDate;
    }

    switch (timeUnit) {
      case 'week': {
        days = 7;
        text = `Weekly Throughput`;
        min = +moment(min).startOf('week');
        max = +new Date(max).endOf('week');
        break;
      }
      case '2weeks': {
        days = 14;
        text = `2 Weeks Throughput`;
        placement = 'between';
        min = +moment(min).startOf('week');
        max = +moment(max).add(14, 'days');
        break;
      }
      case 'month': {
        days = 30;
        ordinal = true;
        text = `Monthly Throughput`;
        min = +moment(min).date(1);
        max = +moment(max).date(30);
        break;
      }
      default: {
        min = +min;
        max = +max;
        break;
      }
    }

    this.chart.update({
      xAxis: {
        ordinal
      },
      yAxis: {
        title: {
          text: text
        }
      },
      plotOptions: {
        column: {
          pointRange: days * 24 * 3600 * 1000,
          pointPlacement: placement
        }
      }
    });

    this.updateChartRange(min, max);
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateChartRange(minVal, maxVal) {
    const min = minVal || +this.props.dates.startDate;
    const max = maxVal || +this.props.dates.endDate;

    const xExtremes = {
      min,
      max
    };
    this.setExtremes(xExtremes);
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('trc', this.props.wallmode);
    this.chart.update(newConfig.chart);
  }

  setExtremes(xExtremesObject, yExtremesObject) {
    if (xExtremesObject) {
      this.chart.xAxis[0].setExtremes(xExtremesObject.min, xExtremesObject.max);
    }
    if (yExtremesObject) {
      this.chart.yAxis[0].setExtremes(yExtremesObject.min, yExtremesObject.max);
    } else {
      this.chart.yAxis[0].setExtremes(null, null);
    }
  }

  getDateString(date) {
    let str = moment(date).format(this.props.dateFormat);
    if (this.props.timeUnit === 'week') {
      str = `${moment(date)
        .startOf('isoWeek')
        .format(this.props.dateFormat)} - ${moment(date)
        .endOf('isoWeek')
        .format(this.props.dateFormat)}`;
    } else if (this.props.timeUnit === '2weeks') {
      str = `${moment(date).format(this.props.dateFormat)} - ${moment(date)
        .add(13, 'days')
        .format(this.props.dateFormat)}`;
    } else if (this.props.timeUnit === 'month') {
      str = `${moment(date)
        .startOf('month')
        .format(this.props.dateFormat)} - ${moment(date)
        .endOf('month')
        .format(this.props.dateFormat)}`;
    }

    return str;
  }

  tooltipFormatter(chartScope, componentScope) {
    let details = '',
      total = 0,
      totalCt = 0;
    for (let p = 0; p < chartScope.points.length; p++) {
      if (chartScope.points[p].point.details) {
        total += chartScope.points[p].y;
        totalCt += chartScope.points[p].point.cycleTime;
        for (let i = 0; i < chartScope.points[p].point.details.length; i++) {
          let point = chartScope.points[p].point.details[i];
          let color = point.color || chartScope.points[p].point.color;
          const isBgWhite = isWhite(color) ? 'border: 1px solid #00408026;' : '';
          details += `<div style="position: relative; width: 270px; margin: 4px 10px 8px; padding-left: 16px; white-space: pre-wrap;"><div style="position: absolute; top: 3px; left:0; margin-left: 1px; display: inline-block; border-radius: 50%; width: 8px; height: 8px; margin-right: 6px; box-sizing: content-box; ${isBgWhite} background: ${color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    const totalDuration = moment
      .duration(totalCt, 'minutes')
      .format(componentScope.props.timePrecision, { trim: 'mid both' });
    const date = componentScope.getDateString(chartScope.x);

    let tooltip = `<p style="font-size: 13px; margin: 10px 10px 0;">Date: ${date}</p> 
      <p style="margin: 8px 10px 0; font-size: 13px;">Throughput: ${total}</p>
      <p style="margin: 8px 10px 0; font-size: 13px;">Cycle time: ${totalDuration}</p>`;
    tooltip += `<hr style="margin: 10px"/>`;
    tooltip += details;
    tooltip += `<p style="padding-left: 10px; color: #999; font-size: 12px;">Click on the bar to see the details</p>`;

    return tooltip;
  }

  onPointClick(comp, ev) {
    const points = [];
    const date = comp.getDateString(ev.x);
    let total = 0,
      totalCt = 0;

    for (let s = 0; s < this.chart.series.length; s++) {
      for (let p = 0; !!this.chart.series[s].points && p < this.chart.series[s].points.length; p++) {
        if (ev.x == this.chart.series[s].points[p].x && this.chart.series[s].points[p].details) {
          total += this.chart.series[s].points[p].y;
          totalCt += this.chart.series[s].points[p].cycleTime;

          for (let i = 0; i < this.chart.series[s].points[p].details.length; i++) {
            const point = this.chart.series[s].points[p].details[i];
            points.push({
              card: point.card,
              details: point.details,
              color: point.color || this.chart.series[s].color
            });
          }
        }
      }
    }

    const totalDuration = moment.duration(totalCt, 'minutes').format(comp.props.timePrecision, { trim: 'mid both' });

    comp.props.onPointClick({
      date,
      value: `${total} item${total != 1 ? 's' : ''}`,
      value2: totalDuration,
      points
    });
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div id={`${this.props.id}-details`} className={'chart_details chart_details-full no-stroke'}></div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    timePrecision: state.filters.timePrecision,
    timeUnit: state.filters.trc.timeUnit,
    showNavigator: state.filters.trc.showNavigator,
    trendLine: state.filters.trc.trendLine,
    breakdown: state.filters.trc.breakdown,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps)(ThroughputChart));
